import { useMemo } from 'react';
import { useCategories } from './api/use-get-categories';
import { useBuckets } from '../bucket/api/use-get-buckets';
// Hook to get categories with buckets
export const useCategoriesWithBuckets = () => {
    const categories = useCategories();
    const buckets = useBuckets();
    return useMemo(() => {
        // Create a map of categoryId to buckets
        const bucketsByCategoryId = buckets.reduce((acc, bucket) => {
            const { categoryId } = bucket;
            if (categoryId) {
                if (!acc[categoryId]) {
                    acc[categoryId] = [];
                }
                acc[categoryId].push(bucket);
            }
            return acc;
        }, {});
        // Map categories to include their buckets
        return categories.map((category) => ({
            ...category,
            buckets: bucketsByCategoryId[category.id] || [],
        }));
    }, [categories, buckets]);
};
/**
 * Constructs categories with their buckets and filters based on viewId if provided.
 *
 * @param {Bucket[]} buckets - The list of all buckets.
 * @param {Category[]} categories - The list of all categories.
 * @param {View[]} views - The list of available views.
 * @param {string | null} viewId - The currently selected view ID.
 *
 * @returns {CategoryWithBuckets[]} - Categories with buckets, filtered based on viewId.
 */
export const constructCategoriesWithBuckets = (buckets, categories, views, viewId) => {
    // Find the selected view if viewId is provided
    const selectedView = viewId ? views.find((view) => view.id === viewId) : null;
    const selectedBucketIds = selectedView ? new Set(selectedView.bucketIds) : null;
    // Create a map of categoryId to buckets, applying the view filter if available
    const bucketsByCategoryId = buckets.reduce((acc, bucket) => {
        const { categoryId } = bucket;
        if (categoryId) {
            // If viewId is provided, only include the bucket if it's in the selected view
            if (selectedBucketIds && !selectedBucketIds.has(bucket.id)) {
                return acc; // Skip this bucket if it's not in the selected view
            }
            if (!acc[categoryId]) {
                acc[categoryId] = [];
            }
            acc[categoryId].push(bucket);
        }
        return acc;
    }, {});
    // Map categories to include their filtered buckets and remove empty categories
    return categories.reduce((acc, category) => {
        const bucketsForCategory = bucketsByCategoryId[category.id] || [];
        if (viewId == null || bucketsForCategory.length > 0) {
            acc.push({
                ...category,
                buckets: bucketsForCategory,
            });
        }
        return acc;
    }, []);
};
